import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ArrowUpRight } from "lucide-react";
import { getAvatarColor } from "../spacesPresenceUtils";
import { formatDuration } from "../spacesTimeUtils";
import {
  SpaceRecapHeader,
  SpaceRecapMetrics,
  SpaceRecapStatus,
} from "../components/SpaceRecapLayout";
import { useSpaceRecap } from "../hooks/useSpaceRecap";

type SpaceRecap = {
  spaceId: string;
  spaceName: string;
  description: string | null;
  lessonContext: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number;
  participantCount: number;
  enrolledCount: number;
  avgTimeSpentMs: number;
  students: { userId: number; name: string; timeSpentMs: number }[];
};

export default function TeacherRecapPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const { status, data, retry } = useSpaceRecap<SpaceRecap>(
    `/spaces/${spaceId}/recap`,
  );
  return (
    <div className="et-space-recap">
      <button
        type="button"
        className="et-recap-back"
        onClick={() => navigate("/spaces")}
      >
        <ChevronLeft size={16} />
        Back to Spaces
      </button>
      <SpaceRecapStatus status={status} onRetry={retry} />
      {status === "ready" && data && (
        <>
          <SpaceRecapHeader
            name={data.spaceName}
            startedAt={data.startedAt}
            onViewCanvas={() => navigate(`/spaces/${spaceId}`)}
          />
          {data.description && (
            <section className="et-recap-objective">
              <h2>Learning objective</h2>
              <p>{data.description}</p>
            </section>
          )}
          <SpaceRecapMetrics
            items={[
              {
                label: "Participants",
                value: (
                  <>
                    {data.participantCount}
                    <small>
                      {" "}
                      / {data.enrolledCount > 0 ? data.enrolledCount : "—"}
                    </small>
                  </>
                ),
              },
              {
                label: "Average time spent",
                value: formatDuration(data.avgTimeSpentMs),
              },
              { label: "Duration", value: formatDuration(data.durationMs) },
            ]}
          />
          <section
            className="et-recap-students"
            aria-labelledby="recap-students-heading"
          >
            <div className="et-recap-section-heading">
              <h2 id="recap-students-heading">Students</h2>
              <span>
                {data.students.length} participant
                {data.students.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="et-recap-panel">
              {data.students.length === 0 ? (
                <p className="et-recap-empty">No students joined this space.</p>
              ) : (
                <ul>
                  {data.students.map((student) => {
                    const color = getAvatarColor(student.userId);
                    return (
                      <li key={student.userId} className="et-recap-student-row">
                        <span
                          className="et-recap-avatar"
                          aria-hidden="true"
                          style={{
                            backgroundColor: color.bg,
                            color: color.text,
                          }}
                        >
                          {student.name.charAt(0).toUpperCase()}
                        </span>
                        <div className="et-recap-student-name">
                          <strong>{student.name}</strong>
                          <span>
                            {formatDuration(student.timeSpentMs)} time spent
                          </span>
                        </div>
                        <button
                          type="button"
                          aria-label={`View work by ${student.name}`}
                          onClick={() =>
                            navigate(
                              `/spaces/${spaceId}/snapshot/${student.userId}`,
                            )
                          }
                        >
                          View work
                          <ArrowUpRight size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
          {data.lessonContext && (
            <details className="et-recap-objective">
              <summary>Instructions to AI</summary>
              <p>{data.lessonContext}</p>
            </details>
          )}
        </>
      )}
    </div>
  );
}
